import type {Theme} from '@emotion/react';
import type {FC, ReactNode, CSSProperties} from 'react';
import type {Root} from 'react-dom/client';

import type {ResearchTheme} from '@gem-research/userProfile/interfaces';
import type {TableTheme} from 'app/components/apiTable/interfaces';
import type {BranchSearchTheme} from 'app/components/branchSearchOld/interfaces';
import type {HeaderTheme} from 'app/components/header/interface';
import type {SectionWithHeadlineTheme} from 'app/components/sectionWithHeadline/interface';
import type {ModalTheme} from 'app/core/modal/interface';
import type {AutoCompleteItemGroup, SearchInputTheme} from 'app/core/searchInput/interfaces';
import type {GemStoreInstance} from 'app/gemStore';
import {type GemSettings, type SiteTheme, type SkinId} from 'app/utils/config/interface';
import type {ComponentMountDataTransporter} from 'app/utils/initialize/interface';
import {type THEMES} from 'app/utils/theme/constants';
import type {ComponentConfig} from 'interfaces/foundation/components/componentConfig';
import type {RemotesConfiguration} from 'interfaces/foundation/styles/remotesConfiguration';

import type {UserProfileTheme} from './components/userProfile/interface';
import type {LoadSuggestionsOptions} from './core/searchAutocompleteCommon/hooks/types';
import type {SearchConfigMio} from './core/searchAutocompleteCommon/interface';

export type Nullable<T> = T | null;
export type Undef<T> = T | undefined;
export type Never<T> = T | never;
export type Nil<T> = T | null | undefined;

type EsRequiredFieldsKey = 'fields';
export type EsRequiredFields = Record<EsRequiredFieldsKey, string[]>;

export interface EsBodyRequest<T = never> {
    filter?: (EsFilterItem | T)[];
    query: string;
    items: number;
    page: number;
    sort?: EsSorting; // if not set, sorting is random
    sortBy?: string[];
    randomScoreSeed?: number; // set only if sorting is random
    fields?: string[];
    queryType?: EsQueryType;
    requiredFields?: EsRequiredFields[];
    aggregations?: {type: string; field: string}[];
}

export interface EsFilterItem {
    key: string;
    value: string;
}

export enum EsSorting {
    RELEVANCE_DATE = 'RELEVANCE_DATE', // 1
    DATE_RELEVANCE = 'DATE_RELEVANCE', // 2
    TITLE = 'TITLE', // 3
    PRODUCT_FAMILY_PRODUCT_SUBTYPE_NAME = 'PRODUCT_FAMILY_PRODUCT_SUBTYPE_NAME', // 4
    TEASER_HEADLINE = 'TEASER_HEADLINE', // 5
    TEASER_SORT_ORDER = 'TEASER_SORT_ORDER', // 6,
    RELEVANCE_TITLE = 'RELEVANCE_TITLE', // help center
    RELEVANCE_PAGE_ORDER_TITLE = 'RELEVANCE_PAGE_ORDER_TITLE', // help center
    PAGE_ORDER_TITLE = 'PAGE_ORDER_TITLE',
    NEWS = 'NEWS',
}

export enum EsQueryType {
    NOT_ANALYZED = 'NOT_ANALYZED',
}

export interface HeaderParameters {
    name: string;
    value: string;
}

export enum EventKey {
    ENTER = 'Enter',
    ESC = 'Escape',
    SPACE = 'Space',
    END = 'End',
    HOME = 'Home',
    LEFT = 'ArrowLeft',
    UP = 'ArrowUp',
    RIGHT = 'ArrowRight',
    DOWN = 'ArrowDown',
    TAB = 'Tab',
}

type InitFnParams<T1> = {
    element: HTMLElement;
    config: T1;
    gemSettings: GemSettings;
    gemStore?: GemStoreInstance;
};

export type InitFn<T1, T2 = HTMLElement> = (params: InitFnParams<T1>) => T2;
export type DeprecatedModuleInitFn<T> = (params: {element: Undef<T>; gemSettings: GemSettings}) => void;

/**
 * LOAD_LAZY - Load component lazy
 * LOAD_EAGER - Load component immediately, skip lazy loading
 * LOAD_EAGER_WITH_SCROLL_TO - Same as LOAD_EAGER, but also scroll to component on its initialization
 */
export type ShouldSkipLazyLoadType = 'LOAD_LAZY' | 'LOAD_EAGER' | 'LOAD_EAGER_WITH_SCROLL_TO';
export type ShouldSkipLazyLoadFn<T = ComponentConfig> = (params: {element: HTMLElement; config?: T}) => ShouldSkipLazyLoadType;

export type ReactLazyComponentParams<T = ComponentConfig | undefined> = {
    config: T;
    gemSettings: GemSettings;
};

export interface GemModuleItem {
    import?: ({element, config, gemSettings}: {element: HTMLElement; config: unknown; gemSettings: GemSettings}) => Promise<unknown>;
    reactLazyComponent?: (params: ReactLazyComponentParams) => ComponentMountDataTransporter['ReactLazyComponent'];
    hasConfig?: boolean;
    shouldSkipLazyLoad?: ShouldSkipLazyLoadFn;
    removePortalContent?: boolean;
}

export interface GemModuleItems {
    [key: string]: GemModuleItem;
}

export type GemModule = Undef<{init: InitFn<ComponentConfig | undefined, Element | Root | undefined>}>;

export type ThemeName = keyof typeof THEMES;

declare module '@emotion/react' {
    interface Theme {
        name?: ThemeName;
        /** Theme class names for usage in HTML className attribute, e.g. `theme theme-white` */
        nameClassNames?: string;
        style?: SiteTheme;
        skin?: SkinId;
        apiTable?: TableTheme;
        modal?: ModalTheme;
        branchSearch?: BranchSearchTheme;
        research?: ResearchTheme;
        sectionWithHeadline?: SectionWithHeadlineTheme;
        header?: HeaderTheme;
        searchInput?: SearchInputTheme;
        userProfile?: UserProfileTheme;
    }
}

declare module 'react' {
    interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
        inert?: boolean;
    }
}

export interface ComponentTheme {
    theme?: Theme;
}

export type FCWithChildren<T = Record<string, unknown>> = FC<T & {children?: ReactNode}>;

export type ESSearchResult<T = Record<string, any>> = {
    total: number;
    items: ESSearchItem<T>[];
    suggestions: string[];
    aggregations: Record<string, ESBucket[]>;
};

export type ESSearchItem<T> = {
    id: string;
    data: T;
    highlight?: Record<string, string[]>;
};

export type ESBucket = {
    key: string;
    docCount: number;
};

export type CSSPropertiesWithCustomProps = CSSProperties & Record<`--${string}`, string | number | undefined>;

export type RemoteModules = {
    init?: (params: InitParams) => void;
    getMioSearchConfiguration?: (
        searchConfig: SearchConfigMio,
        abortController: AbortController,
        gemSettings: GemSettings,
        loadSuggestionsOptions?: LoadSuggestionsOptions,
    ) => Promise<AutoCompleteItemGroup>[];
    defineWebComponents?: (gemSettings: GemSettings) => void;
};

export type InitParams = {
    gemSettings: GemSettings;
    gemStore?: GemStoreInstance;
};

declare global {
    interface Window {
        GEM_REMOTES_CONFIGURATION?: RemotesConfiguration;
    }
}

export type BabelPxToRem = `@pxToRem(${number})`;
export type BabelBreakpoint = `@breakpoint(${'xxs' | 'xs' | 'ms' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl'})`;
